// ============================================================================
// THE MOBILE GLYPH SET — every path transcribed VERBATIM from
// docs/design/handoff-mobile/"LifeOS Mobile.dc.html".
//
// WHY NOT components/icons.tsx. The desktop set is drawn for 18px at stroke 1.7;
// these are drawn for 16–24px at stroke 1.8–2.3 and several of them are simply
// different glyphs (the mobile calendar has two tabs and a header rule, the
// desktop one does not). The mobile README §Assets says "substitute the
// codebase's icon set at the sizes above, one family only" — this IS that
// family, and copying the mockup's own `d` attributes rather than eyeballing a
// substitution is what FIDELITY rule 2 asks for.
//
// STROKE IS `currentColor`, NEVER A LITERAL. The mockup writes each glyph's
// domain hue into a `stroke=` attribute inline; a component cannot
// (scripts/ship-gate.sh fails on a raw hex outside index.css), and it should not
// — an icon that carries its own colour cannot be the same icon on a dark tile.
// Colour arrives as a `text-<token>` class instead.
// ============================================================================

export interface MobileIconProps {
  size?: number
  strokeWidth?: number
  className?: string
}

function Glyph({
  d,
  size = 22,
  strokeWidth = 1.9,
  className,
}: MobileIconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

// ── Tab bar (22px, stroke 1.9) ──────────────────────────────────────────────

export const MSun = (p: MobileIconProps) => (
  <Glyph
    {...p}
    d="M12 6.8a5.2 5.2 0 1 1 0 10.4 5.2 5.2 0 0 1 0-10.4M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M5.1 5.1l1.6 1.6M17.3 17.3l1.6 1.6M18.9 5.1l-1.6 1.6M6.7 17.3l-1.6 1.6"
  />
)

/**
 * A generic date/calendar glyph — no longer School's. See `MGraduationCap` below (owner request,
 * 2026-08-29, mirrors the desktop rail's School/Calendar swap in components/icons.tsx).
 */
export const MCalendar = (p: MobileIconProps) => (
  <Glyph
    {...p}
    d="M4 7.4A2.4 2.4 0 0 1 6.4 5h11.2A2.4 2.4 0 0 1 20 7.4v11.2A2.4 2.4 0 0 1 17.6 21H6.4A2.4 2.4 0 0 1 4 18.6zM4 10h16M8.6 3v3.6M15.4 3v3.6"
  />
)

/** School — the mortarboard, the same path as `GraduationCap` in components/icons.tsx. */
export const MGraduationCap = (p: MobileIconProps) => (
  <Glyph {...p} d="M3 9.6 12 5.2l9 4.4-9 4.4zM7 11.9v4.4c0 1.3 2.2 2.3 5 2.3s5-1 5-2.3v-4.4M20 10.6v4.6" />
)

export const MCheckSquare = (p: MobileIconProps) => (
  <Glyph
    {...p}
    d="M4 6.6A2.6 2.6 0 0 1 6.6 4h10.8A2.6 2.6 0 0 1 20 6.6v10.8A2.6 2.6 0 0 1 17.4 20H6.6A2.6 2.6 0 0 1 4 17.4zM8.4 12.2l2.6 2.6 4.8-5"
  />
)

export const MChatBubble = (p: MobileIconProps) => (
  <Glyph {...p} d="M4 6.4A2.4 2.4 0 0 1 6.4 4h11.2A2.4 2.4 0 0 1 20 6.4v7.2a2.4 2.4 0 0 1-2.4 2.4H9l-5 4zM8.4 10h7.2" />
)

// ── The capture button and the composer ─────────────────────────────────────

export const MPlus = (p: MobileIconProps) => <Glyph size={24} strokeWidth={2.1} {...p} d="M12 5.5v13M5.5 12h13" />

export const MMic = (p: MobileIconProps) => (
  <Glyph
    size={22}
    strokeWidth={1.8}
    {...p}
    d="M12 3.6a2.7 2.7 0 0 1 2.7 2.7v5.4a2.7 2.7 0 0 1-5.4 0V6.3A2.7 2.7 0 0 1 12 3.6M6 11.2a6 6 0 0 0 12 0M12 17.2V21"
  />
)

export const MJournal = (p: MobileIconProps) => (
  <Glyph
    size={16}
    strokeWidth={1.8}
    {...p}
    d="M5 4.6A1.6 1.6 0 0 1 6.6 3H18.4a.6.6 0 0 1 .6.6v16.8a.6.6 0 0 1-.6.6H6.6A1.6 1.6 0 0 1 5 19.4zM5 17.6h14M8.6 7.4h6.4M8.6 11h6.4"
  />
)

// ── Navigation and controls ─────────────────────────────────────────────────

/** The capture sheet's save-and-open tile (20px, 1.9). */
export const MArrowRight = (p: MobileIconProps) => <Glyph size={20} strokeWidth={1.9} {...p} d="M5 12h14M13 6l6 6-6 6" />

/** The chat composer's send tile (18px, 1.9) — a shorter shaft than the 20px one. */
export const MSend = (p: MobileIconProps) => <Glyph size={18} strokeWidth={1.9} {...p} d="M5 12h13M12 6l6 6-6 6" />

export const MSearch = (p: MobileIconProps) => (
  <Glyph size={18} strokeWidth={1.9} {...p} d="M10.6 4a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2M15.3 15.3 20 20" />
)

export const MChevronRight = (p: MobileIconProps) => <Glyph size={18} strokeWidth={2} {...p} d="M10 6l6 6-6 6" />

export const MChevronLeft = (p: MobileIconProps) => <Glyph size={20} strokeWidth={2} {...p} d="M14 6l-6 6 6 6" />

export const MChevronDown = (p: MobileIconProps) => <Glyph size={14} strokeWidth={2} {...p} d="M6 9.5l6 6 6-6" />

/** Three lines — the chat's thread menu (owner, 2026-09-12: "a little button on the top right that looks like a menu"). */
export const MMenu = (p: MobileIconProps) => <Glyph size={22} strokeWidth={1.9} {...p} d="M4 7h16M4 12h16M4 17h16" />

export const MInfo = (p: MobileIconProps) => (
  <Glyph size={17} strokeWidth={1.8} {...p} d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4M12 8.2v4.6M12 15.8v.2" />
)

export const MCheck = (p: MobileIconProps) => <Glyph size={17} strokeWidth={2.2} {...p} d="M5 12.6l4.4 4.4L19 7.4" />

/**
 * The Today band's notifications tile (18px, 1.9).
 *
 * The SAME `d` as `Bell` in components/icons.tsx, which is the glyph the whole app already
 * means "Reminders / notifications" with. The mobile mockup draws no bell — it draws no
 * settings entry at all — so there is no path to transcribe, and inventing a second bell when
 * one already exists is exactly what README §Assets forbids ("one family only"). Redrawn here
 * rather than imported for the reason the header of this file gives: the desktop set defaults
 * to 18px/1.7 and this family carries its own size and weight.
 */
export const MBell = (p: MobileIconProps) => (
  <Glyph
    size={18}
    strokeWidth={1.9}
    {...p}
    d="M9.4 18.4a2.7 2.7 0 0 0 5.2 0M6.2 18.4h11.6l-1.6-2.4v-5a4.2 4.2 0 0 0-8.4 0v5z"
  />
)
